import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Upload } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Categories = () => {
  const { authHeader } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', emoji: '', image: '' });

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, image: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Category name required'); return; }
    try {
      if (editing) {
        const { data } = await axios.put(`${API}/categories/${editing._id}`, form, { headers: authHeader });
        setCategories(prev => prev.map(c => c._id === editing._id ? data : c));
        toast.success('Category updated');
      } else {
        const { data } = await axios.post(`${API}/categories`, form, { headers: authHeader });
        setCategories(prev => [...prev, data]);
        toast.success('Category added');
      }
      setShowForm(false); setEditing(null); setForm({ name: '', emoji: '', image: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: authHeader });
      setCategories(prev => prev.filter(c => c._id !== id));
      toast.success('Category deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (cat) => {
    setEditing(cat); setShowForm(true);
    setForm({ name: cat.name, emoji: cat.emoji || '', image: cat.image || '' });
  };

  return (
    <div className="p-6" data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Categories</h1>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', emoji: '', image: '' }); }}
          className="flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors" data-testid="add-category-button">
          <Plus size={16} /> Add Category
        </button>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" data-testid="category-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-playfair font-bold text-[#1F1410]">{editing ? 'Edit' : 'Add'} Category</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-[#FDF8F3] rounded-full"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Category name"
                  className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="category-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Emoji</label>
                <input value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} placeholder="🎂"
                  className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="category-emoji-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Image</label>
                {form.image && <img src={form.image} alt="Preview" className="w-full h-24 object-cover rounded-lg mb-2 border border-[#ECE0DA]" onError={e => { e.target.style.display = 'none'; }} />}
                <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-[#ECE0DA] rounded-xl cursor-pointer hover:border-[#7A1125] transition-colors">
                  <Upload size={14} className="text-[#7A6A63]" />
                  <span className="text-sm text-[#7A6A63]">Upload image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="flex-1 py-2.5 border border-[#ECE0DA] rounded-full text-sm text-[#7A6A63] hover:bg-[#FDF8F3] transition-colors">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-2.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold rounded-full transition-colors" data-testid="save-category-button">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>
        ) : categories.map(cat => (
          <div key={cat._id} className="bg-white rounded-2xl border border-[#ECE0DA] overflow-hidden shadow-sm group" data-testid={`category-card-${cat._id}`}>
            <div className="relative h-28 overflow-hidden bg-[#FDF8F3]">
              {cat.image ? (
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl">{cat.emoji}</div>
              )}
            </div>
            <div className="p-3 flex items-center justify-between">
              <div>
                <p className="font-playfair font-semibold text-[#1F1410] text-sm">{cat.name}</p>
                <p className="text-xs text-[#7A6A63]">{cat.emoji}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(cat)} className="p-1.5 rounded-lg hover:bg-[#FDF8F3] text-[#7A6A63] hover:text-[#7A1125] transition-colors" data-testid={`edit-category-${cat._id}`}><Edit size={13} /></button>
                <button onClick={() => handleDelete(cat._id)} className="p-1.5 rounded-lg hover:bg-red-50 text-[#7A6A63] hover:text-red-500 transition-colors" data-testid={`delete-category-${cat._id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Categories;
